import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { DateValidators } from "src/validators/date-validators";
import { iConsumptionKind } from "src/model/interface/iConsumptionKind";
import { ConsumptionKind } from "src/components/shared-components/enum/enumConsumptionKind";

import { NumericFieldValidators } from "src/validators/numeric-field-validators";
import moment from "moment";
import { DateTimeFormats } from "src/components/shared-components/class/general-classes/date-time-formats";
import { AlertService } from "src/service/alert.service";
import { DpBillsService } from "src/service/bills.service";
import { Bills } from "src/model/class/general classes/bill";
import { SelectFieldOption } from "src/components/general components/select-field/select-field.component";
import { UploadFileService } from "src/service/upload-file.service";

@Component({
    selector: "app-form-bills",
    templateUrl: "./form-bills.component.html",
    styleUrls: ["./form-bills.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormBillsComponent implements OnInit {
  public fileSelected: File = null;
  public formBill: UntypedFormGroup = null;
  public arrayConsumptionKind: Array<iConsumptionKind> = [];
  /** Same list for the select field (labels are translation keys) */
  public consumptionTypeOptions: Array<SelectFieldOption> = [];
  public showErrorMessageOnClick: boolean = false;
  public isFileContentChanged = false;

  @Input() okText: string;
  @Input() closeText: string;
  @Input() bill: Bills;
  @Input() isEditBill: boolean;
  @Input() isCreateBill: boolean;
  @Input() deleteFileConfirmationMsg: string;

  @Output() onDownloadFile = new EventEmitter();
  constructor(
    private _fb: UntypedFormBuilder,
    private _alertService: AlertService,
    private _dpBillsService: DpBillsService,
    private _uploadFileService: UploadFileService
  ) { }

  ngOnInit() {
    this.getArrayConsumptionKind();
    this.consumptionTypeOptions = this.arrayConsumptionKind.map((kind) => ({
      value: kind.value,
      label: "dictionary." + kind.key,
    }));
    this.initializeBillsForm();
    this.onSelectedBillLoadedPatchFormVal();
  }

  /**
   *  Handle the file change
   * @param event  The event
   */
  public onFileChange(event: any): void {
    const files = (event.target as HTMLInputElement).files;
    if (files.length > 0) {
      this.fileSelected = files[0];
    }
  }

  /**
   * Save the added bill
   * @returns
   */
  public async createFormData(): Promise<Bills | null> {
    if (this.formBill.valid) {
      const fileData: string = await this._uploadFileService.readFileAsync(
        this.fileSelected
      );

      return this.handleSavingBill(fileData);
    } else {
      return null;
    }
  }

  /**
   *  Check if the file name is empty
   * @returns true if the file name is empty
   */
  public isFileNameNotEmpty(): boolean {
    if (this.bill && this.bill.filename) {
      return this.bill.filename !== "";
    } else {
      return false;
    }
  }

  /**
   * Emit an even on click download file
   */
  public onClickDownloadFile(): void {
    this.onDownloadFile.emit();
  }

  /*   * Delete the file from the bill
   */
  public async onClickDeleteFile(): Promise<void> {
    try {
      const alertResponse: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this.deleteFileConfirmationMsg,
          "",
          ""
        );
      if (alertResponse) {
        const deleteFileResponseData: boolean =
          await this._dpBillsService.deleteBillFile(Number(this.bill.id));
        if (deleteFileResponseData) {
          this.bill.filename = "";
          this.isFileContentChanged = true;
          this.formBill.patchValue({
            uploadedFile: null,
          });
        } else {
          this.isFileContentChanged = false;
        }
      } else {
        this.isFileContentChanged = false;
      }
    } catch (error) {
      this._alertService.presentAlertBillsQuotesCorrespondingMsg(false, "", "");
    }
  }

  /**
   * Gets an array of object containing reading intervals key and value
   */
  private getArrayConsumptionKind(): void {
    this.arrayConsumptionKind = Object.keys(ConsumptionKind)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        key: StaticClass.getConsumptionKindLabel(
          ConsumptionKind[key]
        )?.toLowerCase(),
        value: ConsumptionKind[key],
      }));
  }

  /**
   * Initialize form
   */
  private initializeBillsForm(): void {
    this.formBill = this._fb.group(
      {
        fromDate: [
          "",
          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        toDate: [
          "",
          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        number: [null, Validators.required],
        date: [
          "",
          {
            validators: [
              Validators.required,
              DateValidators.isDateNotEmpty(),
              DateValidators.isDateFormatYYYYMMDDCorrect(),
            ],
          },
        ],
        consumptionType: [ConsumptionKind.ELECTRICITY],
        valueConsumption: [null],
        amount: [
          null,
          {
            validators: [
              Validators.required,
              NumericFieldValidators.isNotCorrectNumericVal,
            ],
          },
        ],
        uploadedFile: [null],
        note: [""],
      },
      { validators: DateValidators.dateRangeValidator }
    );
  }

  /**
   *  Handle creating a new bill
   * @param fileData  The file data
   * @returns
   */
  private handleSavingBill(fileData: string): Bills {
    const createdBill: Bills = this.formBill.value;

    if (fileData) {
      createdBill.uploadedFile = {
        namefile: this.fileSelected.name,
        data: fileData,
      };
    }

    createdBill.amount = createdBill.amount === null ? 0 : createdBill.amount;
    createdBill.valueConsumption =
      createdBill.valueConsumption === null ? 0 : createdBill.valueConsumption;
    createdBill.fromDate = StaticClass.isoDateFormatter(createdBill.fromDate);
    createdBill.toDate = StaticClass.isoDateFormatter(createdBill.toDate);
    createdBill.date = StaticClass.isoDateFormatter(createdBill.date);
    if (fileData) {
      createdBill.filename = this.fileSelected.name;
    } else {
      createdBill.filename = this.bill?.filename;
      // No new file: the control still holds the name of the attachment already saved, not a file to upload
      createdBill.uploadedFile = null;
    }

    return createdBill;
  }

  /**
   * Patch bill form with the selected bill
   */
  private onSelectedBillLoadedPatchFormVal(): void {
    if (this.isEditBill && this.bill) {
      this.formBill.patchValue(
        {
          fromDate: this.inputFieldDateFormat(this.bill.fromDate),
          toDate: this.inputFieldDateFormat(this.bill.toDate),
          number: this.bill.number,
          date: this.inputFieldDateFormat(this.bill.date),
          valueConsumption: this.bill.valueConsumption,
          consumptionType: this.bill.consumptionType,
          amount: this.bill.amount,
          note: this.bill.note,
          uploadedFile: this.bill.filename,
        },
        {
          emitEvent: false,
        }
      );
    }
  }

  /**
   *  Format the date to be displayed in the input field on edit
   * @param date  date to format
   * @returns
   */
  private inputFieldDateFormat(date: string): string {
    return moment(date).format("YYYY-MM-DD");
  }
}
